<script lang="ts">
    import { _ } from 'svelte-i18n';

    export let items: { id: string; label: string }[] = [];
    export let selected: string = '';
    export let onChange: (selected: string) => boolean;
    export let disabled: boolean = false;
    export let width: string = '';
    export let withBorder: boolean = false;
    export let styleMode: 'bordered' | 'non-bordered' | 'underline' = 'bordered';

    styleMode = styleMode ? styleMode : withBorder ? 'bordered' : 'non-bordered';
    export let useDefaultStyles: boolean = true;
    export let valid: boolean = true;

    type SelectEvent = Event & { currentTarget: EventTarget & HTMLSelectElement };

    /**
     * Wartość sprzed rozpoczętej zmiany. `bind:value` podpina się pod `change`, a `input` leci przed
     * `change`, więc handler `input` to jedyny moment, w którym `selected` trzyma jeszcze stary stan.
     * Każde wyjście z `handleChange()` bez zapisu wraca właśnie tutaj - nie do pustej wartości, bo
     * pusta wartość u konsumentów oznacza konkretny stan (np. odebranie wszystkich uprawnień).
     */
    let previous: string = selected;

    function handleInput(): void {
        previous = selected;
    }

    function handleChange(event: SelectEvent): void {
        const value = event.currentTarget.value;

        // Listener od `bind:value` biegnie przed tym handlerem i już nadpisał `selected`, więc każde
        // wyjście bez zapisu musi tę zmianę cofnąć samo - inaczej UI (i rodzic, przez `bind:selected`)
        // pokazuje wartość, której nikt nie zapisał.
        if (disabled || value === '') {
            selected = previous;

            return;
        }

        if (typeof onChange !== 'function') {
            console.warn('Select: onChange nie jest funkcją, cofam wybór');
            selected = previous;

            return;
        }

        // Odmowa to wyłącznie jawne `false`. Część konsumentów (np. SettingsLocales) nie zwraca nic
        // i `undefined` nie może kasować wyboru.
        if (onChange(value) === false) {
            selected = previous;

            return;
        }

        previous = value;
    }
</script>

<select
    class:invalid-input={!valid}
    bind:value={selected}
    class="input-small-transparent {useDefaultStyles ? 'input-small-default' : ''} {withBorder ? 'withBorder' : ''} {styleMode}"
    style={width !== '' ? `width:${width}` : ''}
    class:disabled
    {disabled}
    on:input={handleInput}
    on:change={handleChange}
>
    <!-- Zaznaczeniem steruje wyłącznie `bind:value` (select_option). Atrybutu `selected` na <option>
         tu nie ma celowo: kompilował się do przypisania właściwości `option.selected` z wartością
         zapamiętaną przy tworzeniu opcji, a Svelte nie wie o zaznaczeniu zmienionym przez użytkownika.
         W Chrome flush wchodzi między `input` i `change` tej samej zmiany (w Firefoksie nie), więc te
         przypisania cofały DOM do starej opcji, zanim handler `change` odczytał wartość - i do API
         leciał stan sprzed zmiany. Zob. https://er.automapa.pl/issues/52209 -->
    {#each items as item}
        <option value={item.id}>{$_(item.label)}</option>
    {/each}
</select>

<style>
    .input-small-transparent {
        border: none;
        border-radius: var(--input-small-border-radius);
        background-color: transparent;
        cursor: pointer;
        font-size: var(--font-size-tactical-plan);
        font-family: inherit;
    }
    .input-small-default {
        padding: var(--input-small-padding);
        border: var(--input-small-border);
        border-radius: var(--input-small-border-radius);
        color: var(--input-small-color);
        background-color: #fff;
        cursor: pointer;
    }
    .withBorder {
        border: 1px solid #ccc;
    }
    .underline {
        border-bottom: 1px solid #333;
        width: 75px !important;
        text-align: center;
        border-radius: 0;
        color: #333;
        padding: 2px 0 !important;
        font-weight: 600;
    }
    .disabled {
        background-color: #f7f7f7;
    }
</style>
